import { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { AddIcCall } from "@mui/icons-material";
import { useSettings } from "../../context/SettingsContext";
import { motion, AnimatePresence } from "framer-motion";
import { LogItem, AddCallModal } from "./components";

const MotionCard = motion.create ? motion.create(Card) : motion(Card);

export default function CallLogs({
  logs = [],
  onAddCall,
  currentStatus,
  gradeStatusUIMap = {},
  pipelineStatusUIMap = {},
  updateLead,
}) {
  const theme = useTheme();

  const [localLogs, setLocalLogs] = useState(logs);
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [rnr, setRnr] = useState(false);
  const [callSource, setCallSource] = useState("voicemail");
  const [durationMinutes, setDurationMinutes] = useState("");
  const [durationSeconds, setDurationSeconds] = useState("");

  const [saving, setSaving] = useState(false);

  const submit = async () => {
    // Notes required only when Answered (not RNR)
    if ((!rnr && !notes.trim()) || saving) return;

    try {
      setSaving(true);

      const totalDuration =
        (parseInt(durationMinutes) || 0) * 60 +
        (parseInt(durationSeconds) || 0);

      const newLog = {
        _id: Date.now(),
        call_time: new Date().toISOString(),
        notes: rnr ? (notes.trim() || "Missed Call") : notes.trim(),
        rnr,
        callSource,
        duration: totalDuration > 0 ? totalDuration : null,
        employee_name: "Admin",
      };

      setLocalLogs((prev) => [...prev, newLog]);

      await updateLead("call_logs", {
        notes: rnr ? (notes.trim() || "Missed Call") : notes.trim(),
        rnr,
        callSource,
        duration: totalDuration > 0 ? totalDuration : null,
      });

      setOpen(false);
      setNotes("");
      setRnr(false);
      setCallSource("answered");
      setDurationMinutes("");
      setDurationSeconds("");
    } finally {
      setSaving(false);
    }
  };

  const sortedLogs = useMemo(
    () => [...(localLogs || [])].reverse(),
    [localLogs],
  );

  return (
    <>
      <MotionCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        sx={{
          borderRadius: 4,
          bgcolor: "#FFFFFF",
          boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
          border: "1px solid #F1F5F9",
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2.5}
          >
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 700,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Call History
            </Typography>

            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcCall sx={{ fontSize: 18 }} />}
              onClick={() => setOpen(true)}
              sx={{
                px: 2.5,
                py: 1,
                fontWeight: 600,
                fontSize: 13,
                borderRadius: 2.5,
                textTransform: "none",
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                color: "#FFFFFF",
                boxShadow: "0 4px 14px primary.main",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                },
              }}
            >
              Add Call
            </Button>
          </Stack>

          <Stack spacing={1.5}>
            <AnimatePresence>
              {sortedLogs.length > 0 ? (
                sortedLogs.map((c, idx) => (
                  <LogItem
                    key={c._id}
                    time={c.call_time}
                    rnr={c.rnr}
                    notes={c.notes}
                    employee={c.employee_name}
                    callSourceFromLog={c.callSource}
                    duration={c.duration}
                    index={idx}
                    theme={theme}
                    leadStatus={currentStatus}
                  />
                ))
              ) : (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    py: 5,
                    borderRadius: 3,
                    bgcolor: "#FAFAFA",
                    border: "1px dashed #E2E8F0",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#64748B",
                      textAlign: "center",
                    }}
                  >
                    No call history yet
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 12,
                      mt: 0.5,
                      color: "#94A3B8",
                      textAlign: "center",
                    }}
                  >
                    Calls will appear here once recorded
                  </Typography>
                </Stack>
              )}
            </AnimatePresence>
          </Stack>
        </CardContent>
      </MotionCard>

      <AddCallModal
        open={open}
        onClose={() => setOpen(false)}
        theme={theme}
        notes={notes}
        onNotesChange={setNotes}
        rnr={rnr}
        onRnrChange={setRnr}
        callSource={callSource}
        onCallSourceChange={setCallSource}
        durationMinutes={durationMinutes}
        onDurationMinutesChange={setDurationMinutes}
        durationSeconds={durationSeconds}
        onDurationSecondsChange={setDurationSeconds}
        saving={saving}
        onSubmit={submit}
      />
    </>
  );
}
